import { requestStructuredOutput } from "@/lib/ai/structured-outputs";
import { getSectionTypes } from "@/lib/singularity/singularity";
import { globalStore, singularityStore } from "@/store/store";
import { PageDescription, PageDescriptionV } from "@/types/singularity";
import { getTimeElapsedFunction } from "../generate-homepage";
import {
  getTreeFromSectionDescriptions,
  populatePhantomSectionWithAiData
} from "../sections";
import { addOrUpdatePageInWP } from "@/lib/singularity/wp-page-manager/pages";
import { getBatchRequestsForPage } from "../test-ai-service";
import { BatchResponse, callAIBatch } from "../api/api";
import { Tree } from "@/types/tree";
import defensiveCopy from "@/util/defensive-copy";
import { findNode } from "@/lib/tree/tree";
import { cleanJson } from "@/lib/ai/api";
import { getFieldsToComplete } from "@/lib/ai/ai";
import { replaceImagesInSectionWithAiMutating } from "../images/main";

export async function generatePageInTheBackground(
  pageTitle: string,
  idOfPageInWP: number
) {
  const otherPagesOnSite = singularityStore.pagesInWP.map(page => page.title);
  var timeElapsed = getTimeElapsedFunction();

  const pageDescription = await generatePageDescriptionWithLlm(
    pageTitle,
    otherPagesOnSite
  );
  console.log("generatePageDescriptionWithLlm done", timeElapsed());

  if (!pageDescription) return;

  const pageTree = getTreeFromSectionDescriptions(pageDescription.sections);
  console.log("getTreeFromSectionDescriptions done", pageTree, timeElapsed());

  /**
   * save the page to WP before writing the content, just in case the user leaves the process
   * and then if the user comes back later and opens up the page, it will write the content
   */
  var updatedPage = await addOrUpdatePageInWP({
    id: idOfPageInWP,
    title: pageTitle,
    postType: "page",
    tree: pageTree,
    singularityMeta: {
      singularityInitialSections: pageDescription.sections,
      initialSectionsAlreadyPopulated: false
    }
  });
  console.log("addOrUpdatePageInWP done", updatedPage, timeElapsed());

  /**
   * batch rewrite the sections with the LLM and populate the tree with the response from the llm
   */
  const batchRewriteRequests = getBatchRequestsForPage({
    title: pageTitle,
    tree: pageTree
  });
  console.log(
    "getBatchRequestsForPage done",
    batchRewriteRequests,
    timeElapsed()
  );

  const batchResponses = await callAIBatch(batchRewriteRequests.requests);
  console.log("callAIBatch done", batchResponses, timeElapsed());

  const populatedTree = populateTreeFromBatchResponse(pageTree, batchResponses);
  console.log(
    "populateTreeFromBatchResponse done",
    populatedTree,
    timeElapsed()
  );

  const treeWithImages = await populateTreeWithImages(
    populatedTree
  );

  /**
   * update the rewritten page in WP
   */
  updatedPage = await addOrUpdatePageInWP({
    id: idOfPageInWP,
    title: pageTitle,
    postType: "page",
    tree: treeWithImages,
    singularityMeta: {
      singularityInitialSections: pageDescription.sections,
      initialSectionsAlreadyPopulated: true
    }
  });
  if (!updatedPage) return;
  console.log("addOrUpdatePageInWP done", updatedPage, timeElapsed());

  return updatedPage;
}

export async function generatePageDescriptionWithLlm(
  titleOfPageToGenerate: string,
  titlesOfOtherPagesOnSite: string[]
): Promise<PageDescription | undefined> {
  const websiteName = globalStore.aiSettings.prompt.businessName;
  const websiteDescription = globalStore.aiSettings.prompt.businessDescription;

  const sectionTypesString = getSectionTypes()
    .map(sectionType => `'${sectionType}'`)
    .join(", ");

  var prompt = ``;

  const pagesOnSiteAsString = titlesOfOtherPagesOnSite
    .map(page => `- ${page}`)
    .join("\n");

  prompt = `We have a website with the following pages:
            ${pagesOnSiteAsString}
    
          Website name: ${websiteName}
          Website description: ${websiteDescription}

          You will generate the list of sections for the page page titled "${titleOfPageToGenerate}"

          The page should have an appropriate number of sections. Maybe 8 for a homepage, 3 or 4 for a services page, and just one or two for the contact page. Feel free to deviate from this if you think it makes sense.

          Valid values for 'type' are ${sectionTypesString}. It's important you don't use any other values for 'type'.
          `;

  const result = await requestStructuredOutput(prompt, PageDescriptionV);

  return result;
}

function populateTreeFromBatchResponse(
  tree: Tree,
  sectionRewriteResponses: BatchResponse[]
) {
  const populatedTree = defensiveCopy(tree);

  sectionRewriteResponses.forEach(sectionRewriteResponse => {
    const sectionToOperateOn = findNode(
      populatedTree,
      Number(sectionRewriteResponse.id)
    );
    if (!sectionToOperateOn) return;
    populatePhantomSectionWithAiData(
      sectionToOperateOn,
      cleanJson(sectionRewriteResponse.content ?? ""),
      getFieldsToComplete(sectionToOperateOn),
      populatedTree
    );
  });

  return populatedTree;
}

async function populateTreeWithImages(tree: Tree): Promise<Tree> {
  console.log('populateTreeWithImages started');
  const populatedTree = defensiveCopy(tree);
  
  const promises = populatedTree.root.children.map(section => {
    return replaceImagesInSectionWithAiMutating(section, new AbortController());
  });

  console.warn("awaiting promise.all");
  await Promise.all(promises);
  console.warn("promise.all done");

  console.log('populateTreeWithImages done');

  return populatedTree;
}
